import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, tap } from 'rxjs';

import { OFFER_SERVER_URL, ORDER_SERVER_URL } from '../../config';
import { DataWithTranslation, TranslationType } from '../../shared/models/data-with-translations';
import { TemplateDescription, UfoProductSimulationResponse } from '../../product/models/product';
import { PaginatedResult } from '../../shared/models/paginated-result';
import { FieldOfMillOffer, MillOffer } from '../models/mill-offer';
import { DplScope, LogisticsConstraints, MillOfferNeed4Steel, MillOrder, MillOrderPriceResponse,
	OfferRequest, RequestOrder, ExchangeRateLastResponse } from '../models/need4steel';
import { ProductTranslationService } from '../../product/services/product-translation.service';
import { ProductConfiguration } from '../../product/models/product-configuration';
import { FieldValueObject } from '../../product/components/cards/product-card/utils/field/field';
import {
	ProductCharacteristicsTranslationService
} from '../../shared/services/product-characteristics-translation.services';
import { ContactMailContent } from '../components/contact-mail-content';

const MILL_OFFERS_URL = `${ OFFER_SERVER_URL }/v1/millOffers`;
const CONFIGURATIONS_SIMULATE_URL = `${ OFFER_SERVER_URL }/configurations:simulateUfoProductConfiguration`;
const TEMPLATE_URL = `${ OFFER_SERVER_URL }/v1/template:need4steelsearch`;
const ORDER_SUBMIT_URL = `${ ORDER_SERVER_URL }/v1/orders:submitN4S`;
const EXCHANGE_RATES_LAST_URL = `${ ORDER_SERVER_URL }/v1/exchangerates:last`;
const CONTACT_MAIL_UPLOAD_FILE_URL = `${ OFFER_SERVER_URL }/v1/millOffers/contactMail/fileupload`;
const CONTACT_MAIL_SEND_MAIL_URL = `${ OFFER_SERVER_URL }/v1/millOffers/contactMail/send`;
const NEED4STEEL_OFFER_AVAILABLE_URL = `${ ORDER_SERVER_URL }/v1/n4s/orderhistory:isavailable`;
const NEED4STEEL_ORDERHISTORY_URL = `${ ORDER_SERVER_URL }/v1/n4s/orderhistory:fetchMatchingOffers`;
const NEED4STEEL_LOGISTICAL_ROUTE_URL = `${ OFFER_SERVER_URL }/v1/logisticsConstraints`;

const FIRST_FIVE_ORDER_LIMIT = '5';
@Injectable({ providedIn: 'root' })
export class Need4SteelService {
	millOrder!: MillOrder;
	constructor(
		private readonly http: HttpClient,
		private readonly productCharacteristicsTranslationService: ProductCharacteristicsTranslationService,
		private readonly productTranslationService: ProductTranslationService
	) {}

	getMillOffers(
		fields: readonly FieldOfMillOffer[],
		filter: string | undefined,
		index: number,
		limit: number,
		onlyDistinct: boolean
	) {
		const url = new URL(MILL_OFFERS_URL);
		const params = url.searchParams;
		params.append('fields', fields.join(','));
		if (filter) {
			params.append('filter', filter);
		}
		params.append('page', String(Math.floor(index / limit) + 1));
		params.append('limit', String(limit));

		params.append('distinct', String(onlyDistinct));
		return this.http.get<PaginatedResult<MillOffer>>(url.toString());
	}

	getMillOffer(
		offerId: string,
		translationType?: TranslationType
	) {
		const url = new URL(`${ MILL_OFFERS_URL }/${ offerId }`);
		const params = url.searchParams;
		if (translationType !== undefined) {
			params.append('translation', String(translationType));
		}
		return this.http.get<DataWithTranslation<MillOfferNeed4Steel>>(url.toString()).pipe(map(offer => {
			this.productCharacteristicsTranslationService.add(offer.translations);
			return offer.data;
		}));
	}

	getLogisticalroutes(millCode: string) {
		const url = new URL(NEED4STEEL_LOGISTICAL_ROUTE_URL);
		const params = url.searchParams;
		params.append('millCode', millCode);
		return this.http.get<DataWithTranslation<LogisticsConstraints>>(url.toString())
			// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
			.pipe(map(data => data.data ?? []));
	}

	getPriceForOfferLine(
		offerId: string,
		offerLineId: string,
		millOrder: MillOrder,
		requestSimuPriceNumber: string
	) {
		const url = new URL(`${ MILL_OFFERS_URL }/${ offerId }/item:evaluate/${ offerLineId }`);
		const params = url.searchParams;
		params.append('requestSimuPriceNumber', requestSimuPriceNumber);
		return this.http.post<MillOrderPriceResponse>(
			url.toString(),
			millOrder
		);
	}

	simulateA2CProduct(
		templateSimulationRequest: ProductConfiguration | FieldValueObject,
		translationType?: TranslationType
	) {
		const url = new URL(CONFIGURATIONS_SIMULATE_URL);
		const params = url.searchParams;
		if (translationType !== undefined) {
			params.append('translation', String(translationType));
		}
		return this.http.post<DataWithTranslation<UfoProductSimulationResponse>>(
			url.toString(),
			templateSimulationRequest
		).pipe(
			tap(
				rsp => {
					this.productTranslationService.add(rsp.translations);
				}
			),
			map(
				rsp => rsp.data
			)
		);
	}

	getNeed4SteelTemplate(subCategory: string) {
		const url = new URL(`${ TEMPLATE_URL }?subcategory=${ subCategory }`);
		return this.http.get<TemplateDescription>(url.toString());
	}

	submitOrder(
		order: RequestOrder
	) {
		const url = new URL(ORDER_SUBMIT_URL);
		return this.http.post<RequestOrder>(
			url.toString(),
			order
		);
	}

	getLastExchangeRates() {
		const url = new URL(EXCHANGE_RATES_LAST_URL);
		return this.http.get<ExchangeRateLastResponse>(url.toString());
	}

	uploadFile(file: File, hash: string) {
		const url = new URL(`${ CONTACT_MAIL_UPLOAD_FILE_URL }/${ hash }`);
		const form = new FormData();
		form.append('file', file);
		return this.http.post(url.toString(), form, { responseType: 'text' });
	}

	contactMill(mailContent: ContactMailContent) {
		const url = new URL(CONTACT_MAIL_SEND_MAIL_URL);
		return this.http.post<string>(url.toString(), mailContent);
	}

	isPastOrderHistoryAvailable(offerRequest: OfferRequest) {
		const url = new URL(NEED4STEEL_OFFER_AVAILABLE_URL);
		return this.http.post<boolean>(url.toString(), offerRequest);
	}

	fetchOrdersHistory(requestObject: DplScope) {
		const url = new URL(NEED4STEEL_ORDERHISTORY_URL);
		const params = url.searchParams;
		params.append('limit', FIRST_FIVE_ORDER_LIMIT);
		return this.http.post<Map<string, object>[]>(url.toString(), requestObject);
	}
}
